import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { roles } from '@/modules/auth/roles';
import { hasAnyRole } from '@/modules/auth/utils/hasRoles';
import { CompanyClientStatusBadge } from '@/modules/companies/components/CompanyClientStatusBadge';
import { useDeleteCompanyMutation } from '@/modules/companies/mutations/useDeleteCompanyMutation';
import type { CompanyApi } from '@/modules/companies/types';
import { Button } from '@/modules/ui/components/button/Button';
import { Card, CardHeader } from '@/modules/ui/components/card/Card';
import { DeleteButton } from '@/modules/ui/components/delete-button/DeleteButton';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { isDevEnv, isLocalEnv } from '@/utils/env';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import { DemoResetModal } from './DemoResetModal';

interface CompanyCardProps {
  company: CompanyApi;
}

export const CompanyCard = ({ company }: CompanyCardProps) => {
  const deleteCompanyMutation = useDeleteCompanyMutation();
  const currentUserQuery = useCurrentUserQuery();

  const isDefaultTenant = currentUserQuery.data?.defaultTenantId === company.id;
  const canViewHMDashboard = hasAnyRole(
    [roles.Recruiter, roles.Admin, roles.SuperAdmin],
    currentUserQuery.data?.roles ?? [],
  );
  const isSuperAdmin = hasAnyRole(
    [roles.SuperAdmin],
    currentUserQuery.data?.roles ?? [],
  );

  const handleDelete = () => {
    deleteCompanyMutation.mutate(company.id, {
      onError: (error) => {
        toast.error(
          `Company has not been deleted: ${extractErrorMessage(error)}`,
        );
      },
      onSuccess: () => {
        toast.success('Company has been deleted');
      },
    });
  };

  return (
    <Card>
      <CardHeader className="flex items-center gap-4">
        <Link
          to={`/companies/${company.id}/edit`}
          className="flex min-w-0 items-center gap-2"
        >
          <Subheading className="max-w-40 truncate sm:max-w-[250px] md:max-w-[300px]">
            {company.name}
          </Subheading>
          <CompanyClientStatusBadge status={company.clientStatus} />
        </Link>

        <div className="flex flex-1 justify-end gap-2">
          {canViewHMDashboard && (
            <Button variant="outline" asChild>
              <Link to={`/hm?tenantId=${company.id}`}>View HM Dashboard</Link>
            </Button>
          )}

          {isSuperAdmin && (isDevEnv || isLocalEnv) && (
            <DemoResetModal
              company={company}
              trigger={
                <Button variant="outline" className="text-destructive">
                  Reset
                </Button>
              }
            />
          )}

          <PermissionsGate permissions={[permissions.Companies.Update]}>
            <Button variant="primary" asChild>
              <Link to={`/companies/${company.id}/edit`}>Edit</Link>
            </Button>
          </PermissionsGate>

          {!isDefaultTenant && (
            <PermissionsGate permissions={[permissions.Companies.Delete]}>
              <DeleteButton
                description={`This action cannot be undone. This permanently deletes the ${company.name}.`}
                onDelete={handleDelete}
              />
            </PermissionsGate>
          )}
        </div>
      </CardHeader>
    </Card>
  );
};
